import React from "react";
import { Box, SxProps, Theme, Typography } from "@mui/material";
import moment from "moment-timezone";

import { SearchablePopoverSelect } from "../SearchablePopoverSelect";
import { COUNTRIES } from "../../constants/countries";

export interface ITimeZoneOption {
  /** IANA timezone id, e.g. "Asia/Kolkata". */
  value: string;
  /** Human label, e.g. "Asia/Kolkata (GMT+05:30)". */
  label: string;
  /** Current UTC offset string, e.g. "GMT+05:30". */
  offset: string;
}

/** Builds a display label + offset for an IANA timezone id. */
const toTimeZoneOption = (tz: string): ITimeZoneOption => {
  let offset = "";
  try {
    offset = `GMT${moment.tz(tz).format("Z")}`;
  } catch {
    offset = "";
  }
  return {
    value: tz,
    label: offset ? `${tz} (${offset})` : tz,
    offset,
  };
};

/**
 * Returns the timezone options for a given country ISO code (from COUNTRIES).
 * When no country is provided, returns all unique timezones across COUNTRIES.
 */
export const getCountryTimeZones = (
  countryCode?: string | null,
): ITimeZoneOption[] => {
  let zones: string[] = [];

  if (countryCode) {
    const country = COUNTRIES.find((c) => c.code === countryCode);
    zones = country?.timezones ?? [];
  } else {
    const set = new Set<string>();
    COUNTRIES.forEach((c) => (c.timezones ?? []).forEach((z) => set.add(z)));
    zones = Array.from(set);
  }

  return zones.map(toTimeZoneOption);
};

interface EnrTimeZoneSelectorProps {
  /** Selected IANA timezone id. */
  value?: string | null;
  onChange: (timezone: ITimeZoneOption) => void;
  /** ISO country code (e.g. "IN") used to scope the timezone options. */
  countryCode?: string | null;
  disabled?: boolean;
  /** Error message; also drives the error state. */
  error?: string;
  fullWidth?: boolean;
  label?: string;
  sx?: SxProps<Theme>;
}

/**
 * Searchable timezone dropdown, scoped to the selected country's timezones
 * (sourced from COUNTRIES). Matches CountrySelector styling.
 */
export const EnrTimeZoneSelector = ({
  value,
  onChange,
  countryCode,
  disabled,
  error,
  fullWidth,
  label,
  sx,
}: EnrTimeZoneSelectorProps) => {
  const options = React.useMemo(
    () => getCountryTimeZones(countryCode),
    [countryCode],
  );

  const resolvedValue = React.useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value],
  );

  // When the country changes (or on first load with a country), if the current
  // value isn't one of that country's timezones, auto-select the first one.
  // Countries with multiple timezones therefore default to the first.
  React.useEffect(() => {
    if (disabled) return;
    if (options.length === 0) return;
    const isValid = options.some((o) => o.value === value);
    if (!isValid) {
      onChange(options[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, options]);

  return (
    <SearchablePopoverSelect<ITimeZoneOption>
      items={options}
      value={resolvedValue}
      onChange={onChange}
      fullWidth={fullWidth}
      getKey={(tz) => tz.value}
      getSearchText={(tz) => `${tz.value} ${tz.offset}`}
      renderValue={(tz) =>
        tz ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Typography component="span" noWrap sx={{ color: "rgb(38,38,38)" }}>
              {tz.value}
            </Typography>
            {tz.offset && (
              <Typography component="span" variant="body2" color="text.secondary">
                {tz.offset}
              </Typography>
            )}
          </Box>
        ) : null
      }
      renderItem={(tz) => (
        <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="body2" sx={{ flex: 1, color: "rgb(38,38,38)" }}>
            {tz.value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tz.offset}
          </Typography>
        </Box>
      )}
      placeholder={label || "Timezone"}
      label={label}
      searchPlaceholder="Search timezone"
      disabled={disabled}
      error={!!error}
      helperText={error}
      sx={sx}
    />
  );
};
